import { GitBranch, Download, Store, LoaderCircle } from "lucide-react";

// Simple source icons mapping
export const getSourceIcon = (sourceType: string, size = 16) => {
  switch (sourceType) {
    case "git":
      return <GitBranch size={size} />;
    case "marketplace":
      return <Store size={size} />;
    case "upload":
    default:
      return <Download size={size} />;
  }
};

// Simple formatting function
export const formatDisplayName = (text: string): string => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Simple status config
export const getStatusConfig = (status: string) => {
  switch (status) {
    case "running":
      return {
        label: "Running",
        style: "info" as const,
        icon: null,
      };
    case "missing_config":
      return {
        label: "Missing config",
        style: "warning" as const,
        icon: null,
      };
    case "installing":
      return {
        label: "Installing",
        style: "neutral" as const,
        icon: <LoaderCircle size={14} className="animate-spin" />,
      };
    case "restarting":
      return {
        label: "Restarting",
        style: "neutral" as const,
        icon: <LoaderCircle size={14} className="animate-spin" />,
      };
    case "error":
      return {
        label: "Error",
        style: "error" as const,
        icon: null,
      };
    case "uninstalling":
      return {
        label: "Uninstalling",
        style: "neutral" as const,
        icon: <LoaderCircle size={14} className="animate-spin" />,
      };
    default:
      return {
        label: status,
        style: "neutral" as const,
        icon: null,
      };
  }
};

// Filter options
export const STATUS_FILTER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Installing", value: "installing" },
  { label: "Running", value: "running" },
  { label: "Missing config", value: "missing_config" },
  { label: "Error", value: "error" },
];

export const SOURCE_FILTER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Git", value: "git", icon: getSourceIcon("git", 14) },
  { label: "Upload", value: "upload", icon: getSourceIcon("upload", 14) },
];

export const PLUGIN_CATEGORY_OPTIONS = [
  { label: "All", value: "all" },
  { label: "General", value: "general" },
  { label: "Document", value: "document" },
  { label: "Other", value: "other" },
];

/**
 * Validates if a given URL is a cloneable Git repository URL
 */
export const validateGitUrl = (url: string): string | true => {
  if (!url.trim()) return true;

  const gitClonePatterns = [
    /^https?:\/\/.*\.git$/i, // HTTPS URLs ending with .git
    /^git@.*\.git$/i, // SSH URLs ending with .git
    /^https?:\/\/github\.com\/[\w\-\.]+\/[\w\-\.]+$/i, // GitHub repos
    /^https?:\/\/gitlab\.com\/[\w\-\.]+\/[\w\-\.]+$/i, // GitLab repos
    /^https?:\/\/bitbucket\.org\/[\w\-\.]+\/[\w\-\.]+$/i, // Bitbucket repos
  ];

  const isValid = gitClonePatterns.some((pattern) => pattern.test(url));
  return isValid || "Please enter a cloneable Git repository URL";
};

/**
 * FastAPI's `detail` is either a string (HTTPException) or an array of
 * Pydantic validation errors `[{loc, msg, type}, ...]`. Render both shapes as
 * something a human can act on — never JSON.stringify a validation array.
 *
 * Our custom validators write self-describing messages, so the `loc` path
 * and Pydantic's "Value error, " prefix are pure noise in user-facing
 * toasts — strip both, fall back to the path when the message is the
 * Pydantic generic.
 */
const formatFastApiDetail = (detail: unknown): string => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (d && typeof d === "object" && "msg" in d) {
          const e = d as { loc?: unknown; msg?: unknown };
          const locParts = Array.isArray(e.loc)
            ? e.loc.filter((p) => p !== "body").map(String)
            : [];
          const where = locParts.join(".");
          const rawMsg = String(e.msg ?? "Invalid value");
          const msg = rawMsg.replace(/^Value error,\s*/i, "");
          // Generic Pydantic messages need the path to be useful — keep
          // it as a hint. Anything custom stands on its own.
          const isGeneric = msg === "Invalid value" || msg === "field required";
          return isGeneric && where ? `${where}: ${msg}` : msg;
        }
        return typeof d === "string" ? d : JSON.stringify(d);
      })
      .join("\n");
  }
  return JSON.stringify(detail);
};

/**
 * Extracts a user-friendly error message from various error formats
 */
export const extractErrorMessage = (
  error: any,
  defaultMessage: string = "Unknown error"
): string => {
  // FastAPI returns errors in 'detail' field (axios error structure)
  if (error?.response?.data?.detail) {
    return formatFastApiDetail(error.response.data.detail);
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // @hey-api/client-axios might include data directly on error
  if (error?.data?.detail) {
    return formatFastApiDetail(error.data.detail);
  }

  // openapi-ts client wraps errors differently
  if (error?.error?.detail) {
    return formatFastApiDetail(error.error.detail);
  }

  // Our own error envelope is {identifier, params, message, status_code}, so
  // through this client the text lands on error.error.message — the branches
  // above only look for `detail`, which a raised NoxusError never sets.
  if (error?.error?.message) {
    return String(error.error.message);
  }

  if (error?.data?.message) {
    return String(error.data.message);
  }

  // Skip generic axios messages if we might have better info elsewhere
  if (error?.message && !error.message.includes("status code")) {
    return error.message;
  }

  // Check body for detail (some client configurations)
  if (error?.body?.detail) {
    return formatFastApiDetail(error.body.detail);
  }

  // Last resort - use axios message or default
  if (error?.message) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return defaultMessage;
};
